import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR, { SWRResponse } from 'swr'

import { MAX_PAGE_SIZE } from '@/constants/api'
import { PLATFORM } from '@/constants/platform'
import { useAuthStore } from '@/stores/useAuthStore'
import { useLicenseStore } from '@/stores/useLicenseStore'
import { LabelValue } from '@/types/common'
import { DDEPaginationType } from '@/types/dde/pagination'
import { Competition, Season } from '@/types/dde/season'
import logger from '@/utils/_legacy/logger'
import { seasonToLabelValueObject } from '@/utils/licenseUtils'
import { notifyError } from '@/utils/notifyError'
import { warnDev } from '@/utils/warnDev'

import { DDE_ENDPOINTS } from './constants/ddeEndpoints'
import { fetchData } from './request'

/**
 * Builds a paginated URL with optional filtering
 * @param baseUrl - The base URL to append parameters to
 * @param page - The page number (0-based)
 * @param filter - Optional filter string
 * @returns The constructed URL with pagination and filter parameters
 */
function buildPaginatedUrl(baseUrl: string, page: number, filter?: string): string {
  const params = new URLSearchParams({
    size: MAX_PAGE_SIZE.toString(),
    page: page.toString()
  })

  if (filter) {
    params.set('filter', filter)
  }

  return `${baseUrl}?${params.toString()}`
}

export function useGetSportSeasons(
  sportType: string | null,
  page = 0,
  ftSearch?: string
): SWRResponse<LabelValue[]> {
  const platform = useAuthStore.getState().platform
  const {
    totalCompetitionSeasonsPages,
    setTotalCompetitionSeasonsPages,
    addFetchedCompetitionSeasons
  } = useLicenseStore()

  if (platform === PLATFORM.DDE) {
    const endpointData = sportType ? DDE_ENDPOINTS.Sport.Seasons.get(sportType) : null

    return useSWR<LabelValue[]>(
      endpointData?.url && page <= totalCompetitionSeasonsPages
        ? buildPaginatedUrl(endpointData.url, page, ftSearch)
        : null,
      (url) =>
        fetchData<DDEPaginationType<Season[]>, { [key: string]: string }>({
          endpointData: { url },
          options: { headers: { paged: 'true' } }
        }).then((res) => {
          const mappedSeasons = res.content.map(seasonToLabelValueObject)
          addFetchedCompetitionSeasons(mappedSeasons)

          if (res.totalPages > totalCompetitionSeasonsPages) {
            setTotalCompetitionSeasonsPages(res.totalPages)
          }
          return mappedSeasons
        }),
      {
        revalidateOnFocus: false
      }
    )
  }

  warnDev('useGetSportSeasons is only supported on DDE platform')

  return {
    data: [],
    error: null,
    isValidating: false,
    mutate: () => Promise.resolve([]),
    isLoading: false
  }
}

export function useGetSportSeasonsWithPagination(
  sportType: string | null,
  ftSearch?: string
): {
  data: LabelValue[]
  isLoading: boolean
  isLoadingMore: boolean
  loadMore: (page: number) => void
  hasMore: boolean
  currentPage: number
  totalCompetitionSeasonsPages: number
} {
  const platform = useAuthStore.getState().platform
  const {
    totalCompetitionSeasonsPages,
    setTotalCompetitionSeasonsPages,
    addFetchedCompetitionSeasons,
    fetchedCompetitionSeasons,
    resetFetchedCompetitionSeasons
  } = useLicenseStore()

  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const previousSearchRef = useRef<string | undefined>(undefined)

  const loadMore = useCallback(
    async (page: number) => {
      if (isLoadingMore || !sportType || platform !== PLATFORM.DDE) return

      setIsLoadingMore(true)
      try {
        const endpointData = DDE_ENDPOINTS.Sport.Seasons.get(sportType)
        const url = buildPaginatedUrl(endpointData.url, page, ftSearch)

        const res = await fetchData<DDEPaginationType<Season[]>, { [key: string]: string }>({
          endpointData: { url },
          options: { headers: { paged: 'true' } }
        })

        const mappedSeasons = res.content.map(seasonToLabelValueObject)
        addFetchedCompetitionSeasons(mappedSeasons)

        if (res.totalPages > totalCompetitionSeasonsPages) {
          setTotalCompetitionSeasonsPages(res.totalPages)
        }

        setCurrentPage(page)
        setHasMore(page < res.totalPages - 1)
      } catch (error) {
        logger.error(error, `Error fetching sport seasons for ${sportType}`)
      } finally {
        setIsLoadingMore(false)
      }
    },
    [
      sportType,
      ftSearch,
      isLoadingMore,
      platform,
      addFetchedCompetitionSeasons,
      totalCompetitionSeasonsPages,
      setTotalCompetitionSeasonsPages
    ]
  )

  useEffect(() => {
    resetFetchedCompetitionSeasons()
    setCurrentPage(0)
    setHasMore(true)

    if (sportType && platform === PLATFORM.DDE) {
      const loadFirstPage = async () => {
        setIsLoading(true)
        try {
          const endpointData = DDE_ENDPOINTS.Sport.Seasons.get(sportType)
          const url = buildPaginatedUrl(endpointData.url, 0, ftSearch)

          const res = await fetchData<DDEPaginationType<Season[]>, { [key: string]: string }>({
            endpointData: { url },
            options: { headers: { paged: 'true' } }
          })

          const mappedSeasons = res.content.map(seasonToLabelValueObject)
          addFetchedCompetitionSeasons(mappedSeasons)

          if (res.totalPages > totalCompetitionSeasonsPages) {
            setTotalCompetitionSeasonsPages(res.totalPages)
          }

          setCurrentPage(0)
          setHasMore(res.totalPages > 1)
        } catch (error) {
          logger.error(error, `Error fetching sport seasons for ${sportType}`)
        } finally {
          setIsLoading(false)
        }
      }

      loadFirstPage()
    }
  }, [
    sportType,
    platform,
    resetFetchedCompetitionSeasons,
    addFetchedCompetitionSeasons,
    totalCompetitionSeasonsPages,
    setTotalCompetitionSeasonsPages,
    ftSearch
  ])

  useEffect(() => {
    if (sportType && platform === PLATFORM.DDE) {
      const searchChanged = previousSearchRef.current !== ftSearch

      const loadSearchResults = async () => {
        setIsLoading(true)
        try {
          const endpointData = DDE_ENDPOINTS.Sport.Seasons.get(sportType)
          const url = buildPaginatedUrl(endpointData.url, 0, ftSearch)

          const res = await fetchData<DDEPaginationType<Season[]>, { [key: string]: string }>({
            endpointData: { url },
            options: { headers: { paged: 'true' } }
          })

          if (searchChanged) {
            resetFetchedCompetitionSeasons()
            setCurrentPage(0)
            setHasMore(true)
          }

          const mappedSeasons = res.content.map(seasonToLabelValueObject)
          addFetchedCompetitionSeasons(mappedSeasons)

          if (res.totalPages > totalCompetitionSeasonsPages) {
            setTotalCompetitionSeasonsPages(res.totalPages)
          }

          if (searchChanged) {
            setCurrentPage(0)
            setHasMore(res.totalPages > 1)
          }
        } catch (error) {
          logger.error(error, `Error fetching sport seasons for ${sportType}`)
        } finally {
          setIsLoading(false)
        }
      }

      loadSearchResults()
      previousSearchRef.current = ftSearch
    }
  }, [
    ftSearch,
    sportType,
    platform,
    resetFetchedCompetitionSeasons,
    addFetchedCompetitionSeasons,
    totalCompetitionSeasonsPages,
    setTotalCompetitionSeasonsPages
  ])

  return {
    data: fetchedCompetitionSeasons,
    isLoading,
    isLoadingMore,
    loadMore,
    hasMore,
    currentPage,
    totalCompetitionSeasonsPages
  }
}

export async function getSportCompetitions(sportType: string): Promise<{ data: Competition[] }> {
  const platform = useAuthStore.getState().platform

  if (platform === PLATFORM.DDE) {
    try {
      const response = await fetchData<DDEPaginationType<Competition[]>>({
        endpointData: DDE_ENDPOINTS.Sport.Competitions.get(sportType)
      })

      if (response && typeof response === 'object' && 'content' in response) {
        return { data: response.content }
      }

      return { data: [] }
    } catch (error) {
      logger.error(error, `Error fetching sport competitions for ${sportType}`)
      notifyError(`Error fetching sport competitions for ${sportType}`)
      return { data: [] }
    }
  }

  throw new Error('Unsupported platform')
}
